import React, { useEffect, useState } from "react";
import "./RecordingProfiles.scss";
import { MyContext } from "../../context";
import {
    TextField,
    mergeStyleSets,
    Label,
    ChoiceGroup,
    Checkbox,
    FontWeights,
    ComboBox,
    Text,
    ActionButton,
    Dropdown,
    PrimaryButton,
    CommandBarButton,
    IconButton,
    DefaultButton,
    TooltipHost,
    FontIcon,
    DirectionalHint,
    Toggle,
    TeachingBubble,
    MessageBar,
    MessageBarType,
} from "office-ui-fabric-react";
import NumberPrefix from "../RuleSetter/RuleList/NumberPrefix";
import TimePicker from "react-time-picker";
import ProfileRuleScheduleCard from "./ProfileRuleScheduleCard";
import InputComponent from "./InputComponent";
import { beakLeftOverIcon } from "../common/tooltipBeak";
import { mutationCreateUserProfileRule, queryGetConfigData, mutationUpdateUserProfileRule, mutationDeleteUserProfileRule } from "../../queries/querySettings";
import { toast } from "react-toastify";
import {
    transformTimetableToState,
    transformStateToTimetable,
    transformListToState,
    transformStateToList,
    getSecondsFromTimeString,
    getTimeStringFromSeconds,
    checkDefaultCustomAnnouncement,
    getAnnouncementsWithoutDefault,
    getDefaultAnnouncements,
    getAmountOfConversationsToRecord,
    areSchedulesEqual,
    doesProfileNameExist
} from "./ProfileRuleHelpers";
import UploadAnnouncement from "../RuleSetter/UploadAnnouncement";

const classNames = mergeStyleSets({
    container: {
        padding: 20,
    },
    card: {
        border: "1px solid #ccc",
        borderRadius: 4,
        padding: "10px 20px 20px 20px",
        marginBottom: "20px",
        position: "relative",
    },
    separator: {
        padding: "inherit",
        paddingBottom: "0px",
        margin: "5px 0px 0px -20px",
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
    },
    cardTitle: {
        fontWeight: "600",
        fontSize: "16px",
    },
    cardNumber: {
        backgroundColor: "#898989",
        color: "white",
        borderRadius: "50%",
        width: "20px",
        height: "20px",
        textAlign: "center",
        marginRight: 10,
    },
    columnGrid3: {
        display: "grid",
        gridTemplateColumns: "2fr 3fr 2fr",
    },
    flexContainer: {
        display: "flex",
        alignItems: "center",
    },
    flexContainerEnd: {
        display: "flex",
        alignItems: "end",
    },
    marginBottom: {
        marginBottom: "7px",
    },
    marginTop: {
        marginTop: "10px",
    },
    numberInput: {
        width: "55px",
    },
    actionButton: {
        color: "#316493",
        marginLeft: "-8px",
        marginBottom: "-7px",
    },
});

function getMetadataTtlError(
    metadataTtl,
    audioTtl,
    videoTtl,
    screenTtl,
    context,
    audioIsActive,
    videoIsActive,
    screenIsActive,
    license,
    isEntryVoiceOnly
) {
    if (isNaN(metadataTtl) || metadataTtl === 0) {
        return context.getTranslation("common", "minValue");
    }
    if ((license === "13" || isEntryVoiceOnly) && metadataTtl > 180) {
        return "Maximum 180 days allowed";
    }
    if (
        (audioIsActive && audioTtl !== null && audioTtl > metadataTtl) ||
        (videoIsActive && videoTtl !== null && videoTtl > metadataTtl) ||
        (screenIsActive && screenTtl !== null && screenTtl > metadataTtl)
    ) {
        return context.getTranslation("recordingRule", "metadataMustBeHighest");
    }
    return "";
}

function getMediaTtlError(mediaTtl, metadataTtl, context, isActive, license, isEntryVoiceOnly) {
    if (isActive && (isNaN(mediaTtl) || mediaTtl === 0)) {
        return context.getTranslation("common", "minValue");
    }
    if ((license === "13" || isEntryVoiceOnly) && mediaTtl > 180) {
        return "Maximum 180 days allowed";
    }
    if (isActive && metadataTtl !== null && mediaTtl > metadataTtl) {
        return context.getTranslation("recordingRule", "metadataMustBeHighest");
    }
    return "";
}

const ProfileRuleSettings = ({ apol, authorization, profileRuleData, handleAddOrEditRuleFromProfile, ruleFromProfile, closePanel, accessProfiles, profilesRuleList, userRules, tenantData }) => {
    const styles = {
        root: {
            paddingTop: "20px",
        },
    };

    const [state, setState] = useState({
        // FIRST CARD
        id: profileRuleData?.id || null,
        name: profileRuleData?.name || "",
        mediaTypes: profileRuleData?.filters?.conversationType || [],
        isAllMediaTypes: (!profileRuleData?.filters?.conversationType || profileRuleData.filters.conversationType.length === 0) ? false : true,
        description: profileRuleData?.text || "",
        isEntryVoiceOnly: profileRuleData?.isEntryVoiceOnly || false,
        callQueueRuleOverride: profileRuleData?.callQueueRuleOverride || false,
        graphqlPending: false,
        nameError: "",
        // SECOND CARD
        participantsList: transformListToState("Participants", profileRuleData?.filters?.lists),
        callQueueList: transformListToState("CallQueue", profileRuleData?.filters?.lists),
        meetingOrganizersList: transformListToState("Organizers", profileRuleData?.filters?.lists),
        meetingAttendeesList: transformListToState("Attendees", profileRuleData?.filters?.lists),
        meetingSubjectList: transformListToState("MeetingSubject", profileRuleData?.filters?.lists),
        statisticsRecordingType: profileRuleData?.filters?.statistics?.statisticsRecordingType || "EveryXthConversation",
        amountOfConversations1Input:
            profileRuleData?.filters?.statistics?.statisticsRecordingType === "EveryXthConversation"
                ? profileRuleData?.filters?.statistics?.amountOfConversationsToRecord
                : 0,
        amountOfConversations2Input:
            profileRuleData?.filters?.statistics?.statisticsRecordingType === "PercentOfConversations"
                ? profileRuleData?.filters?.statistics?.amountOfConversationsToRecord
                : 0,
        amountOfConversations3Input:
            profileRuleData?.filters?.statistics?.statisticsRecordingType === "FirstXConversations"
                ? profileRuleData?.filters?.statistics?.amountOfConversationsToRecord
                : 0,
        recordingInterval: profileRuleData?.filters?.statistics?.recordingInterval || "Day",
        //useStatistics: profileRuleData?.filters?.statistics?.useStatistics || false, *****will activate when feature will be implemented***
        useStatistics: false, // for now we will not use statistics
        useSchedulingFilter: profileRuleData?.filters?.statistics?.useSchedulingFilter || false,

        duration: profileRuleData?.filters?.schedulingFilter?.hasDurationFilter || false,

        minDuration:
            profileRuleData?.filters?.schedulingFilter?.duration?.min &&
                profileRuleData.filters.schedulingFilter.duration.min !== "00:00:00"
                ? profileRuleData.filters.schedulingFilter.duration.min
                : "",

        maxDuration:
            profileRuleData?.filters?.schedulingFilter?.duration?.max &&
                profileRuleData.filters.schedulingFilter.duration.max !== "00:00:00"
                ? profileRuleData.filters.schedulingFilter.duration.max
                : "",
        schedule: profileRuleData?.filters?.schedulingFilter?.timetable
            ? transformTimetableToState(profileRuleData.filters.schedulingFilter.timetable)
            : [],
        // Third CARD
        recAudioType: profileRuleData?.filters?.recordingType?.recAudioType || "Never",
        //
        // BULK
        bulk: {
            hasStartStops: profileRuleData?.filters?.recordingType?.bulk?.hasStartStops || false,
            startStops: profileRuleData?.filters?.recordingType?.bulk?.startStops || "Unlimited",
            stopReasonRequired: profileRuleData?.filters?.recordingType?.bulk?.stopReasonRequired || false,
            stopReasons: profileRuleData?.filters?.recordingType?.bulk?.stopReasons || "",
            recordingStartsAfter: profileRuleData?.filters?.recordingType?.bulk?.recordingStartsAfter || 0,
            recordingDelayEnabled: profileRuleData?.filters?.recordingType?.bulk?.recordingDelayEnabled || false,
        },
        // ON DEMAND
        rod: {
            stopReasonRequired: profileRuleData?.filters?.recordingType?.rod?.stopReasonRequired || false,
            stopReasons: profileRuleData?.filters?.recordingType?.rod?.stopReasons || "", // reasons sepparated by "|"
            startStops: profileRuleData?.filters?.recordingType?.rod?.startStops || "Unlimited",
            hasStartStops: profileRuleData?.filters?.recordingType?.rod?.hasStartStops || false,
        },
        // RECORDING CONTROL
        recordingControl: {
            hasStartStops: profileRuleData?.filters?.recordingType?.recordingControl?.hasStartStops || false,
            recordingStartsAfter: profileRuleData?.filters?.recordingType?.recordingControl?.recordingStartsAfter || 0,
            startStops: profileRuleData?.filters?.recordingType?.recordingControl?.startStops || "Unlimited",
            autoStartRecording: profileRuleData?.filters?.recordingType?.recordingControl?.autoStartRecording || false,
            stopReasons: profileRuleData?.filters?.recordingType?.recordingControl?.stopReasons || "",
            stopReasonRequired: profileRuleData?.filters?.recordingType?.recordingControl?.stopReasonRequired || false,
        },

        // KEEP DELETE

        keepDeleteSettings: {
            hasKeepDelete: profileRuleData?.filters?.recordingType?.hasKeepDelete || false,
            startMode: profileRuleData?.filters?.recordingType?.startMode || "Keep",
        },
        //WRAPUP
        hasWrapUp: profileRuleData?.filters?.recordingType?.hasWrapUp || false,
        wrapUp: profileRuleData?.filters?.recordingType?.wrapUp !== undefined
            ? profileRuleData.filters.recordingType.wrapUp
            : null,
        // FOURTH CARD
        media: {
            ttlType: profileRuleData?.filters?.recordingType?.media?.ttlType || null,
            ttl: profileRuleData?.filters?.recordingType?.media?.ttl || null,
            metadata: {
                isActive: true,
                ttl: profileRuleData?.filters?.recordingType?.media?.metadata?.ttl || null,
                isRod: profileRuleData?.filters?.recordingType?.media?.metadata?.isRod || false,
            },
            audio: {
                isActive: profileRuleData?.filters?.recordingType?.media?.audio?.isActive || false,
                ttl: profileRuleData?.filters?.recordingType?.media?.audio?.ttl || null,
                isRod: profileRuleData?.filters?.recordingType?.media?.audio?.isRod || false,
            },
            video: {
                isActive: profileRuleData?.filters?.recordingType?.media?.video?.isActive || false,
                ttl: profileRuleData?.filters?.recordingType?.media?.video?.ttl || null,
                isRod: profileRuleData?.filters?.recordingType?.media?.video?.isRod || false,
            },
            screen: {
                isActive: profileRuleData?.filters?.recordingType?.media?.screen?.isActive || false,
                ttl: profileRuleData?.filters?.recordingType?.media?.screen?.ttl || null,
                isRod: profileRuleData?.filters?.recordingType?.media?.screen?.isRod || false,
            },

        },
        // Fifth CARD
        announcementSetting: profileRuleData?.announcementSetting || "Profile",
        announcementType: profileRuleData?.announcementType || "DefaultAnnouncement",
        announcements: profileRuleData?.announcements ? getAnnouncementsWithoutDefault(profileRuleData?.announcements) : [],
        beepToneDelay: profileRuleData?.beepToneDelay ? profileRuleData?.beepToneDelay : 5,
        enableBeepTone: profileRuleData?.enableBeepTone || false,
        defaultCustomAnnouncement: profileRuleData?.announcements ? checkDefaultCustomAnnouncement(profileRuleData?.announcements) : false,
        deletedFilesFromAnnouncements: [],
        defaultAnnouncement: profileRuleData?.announcements
            ? getDefaultAnnouncements(profileRuleData?.announcements)
            : {
                prefixList: ["default"],
                start: "",
                stop: "",
            },
        numberPrefixIndex: profileRuleData?.announcements ? profileRuleData?.announcements.length : 0,
        showDeleteWarning: false,
    });

    const ruleList = profilesRuleList || [];
    const nameExists = doesProfileNameExist(
        ruleList.filter(p => p.id !== state.id),
        state.name
    );
    const nameErrorMessage = nameExists ? "nameUsed" : "";



    // Mark all schedules that are duplicates of any other schedule, return [{id, isDuplicate}]
    const getIsDuplicateArray = (schedules) => {
        if (schedules.length < 2) return schedules.map(item => ({ id: item.id, isDuplicate: false }));
        return schedules.map((schedA, idxA) => {
            const isDuplicate = schedules.some((schedB, idxB) => idxA !== idxB && areSchedulesEqual(schedA, schedB));
            return { id: schedA.id, isDuplicate };
        });
    };

    const isDuplicateArray = getIsDuplicateArray(state.schedule);

    // STATE MANAGING FUNCTIONS
    const toggleMediaType = (type, isAll = false) => {
        const allMediaTypes = ["Inbound", "Outbound", "Internal", "InternalMeeting", "ExternalMeeting"];
        if (type === "all" && isAll) {
            setState((prevState) => ({
                ...prevState,
                mediaTypes: allMediaTypes,
                isAllMediaTypes: true,
            }));
        } else if (type === "all" && !isAll) {
            setState((prevState) => ({
                ...prevState,
                mediaTypes: [],
                isAllMediaTypes: false,
            }));
        } else {
            setState((prevState) => {
                const mediaTypes = prevState.mediaTypes.includes(type) ? prevState.mediaTypes.filter((t) => t !== type) : [...prevState.mediaTypes, type];
                return { ...prevState, mediaTypes };
            });
        }
    };

    const isScheduleValid = (schedules) => {
        // If scheduling filter is not used, always valid
        if (!state.useSchedulingFilter) return true;
        for (const sched of schedules) {
            const { period, week, interval } = sched.data;
            // If schedule is not enabled, skip validation
            if (period === "weekly" || period === "monthly") {
                const anyDayActive = Object.values(week).some(day => day.isActive);
                if (!anyDayActive) return false;
            }
            // If period is monthly, interval must not be empty or null
            if (period === "monthly") {
                if (!interval) {
                    return false;
                }
            }
        }

        // --- Duplicate schedule validation ---
        if (state.useSchedulingFilter && isDuplicateArray.some(d => d.isDuplicate)) {
            return false;
        }
        return true;
    };

    const isFormValid = () => {
        // Name and mediaTypes are always required
        if (state.name.trim() === "") {
            return false;
        }

        // Duplicate profile name validation
        if (nameExists) return false;

        // --- Duplicate schedule validation ---
        if (state.useSchedulingFilter && isDuplicateArray.some(d => d.isDuplicate)) {
            return false;
        }

        // If any filter's checkbox is checked, require at least one entry ---
        const filterLists = [
            { list: state.participantsList, label: "participantsList" },
            { list: state.callQueueList, label: "callQueueList" },
            { list: state.meetingOrganizersList, label: "meetingOrganizersList" },
            { list: state.meetingAttendeesList, label: "meetingAttendeesList" },
            { list: state.meetingSubjectList, label: "meetingSubjectList" }
        ];
        for (const { list } of filterLists) {
            // If the filter has at least one item and its checkbox is checked (isActive), require at least one entry
            for (const item of list) {
                if (item.data && item.data.isActive) {
                    // Check if entries is a non-empty array or non-empty string
                    const entries = item.data.entries;
                    if (
                        !entries ||
                        (Array.isArray(entries) && entries.length === 0) ||
                        (typeof entries === "string" && entries.trim() === "")
                    ) {
                        return false;
                    }
                }
            }
        }

        // If recAudioType is not Never, require at least one media item with a valid retention time
        if (state.recAudioType !== "Never") {
            // At least one of audio, video, or screen must be selected (checked)
            const hasAnyMediaChecked =
                state.media.audio.isActive ||
                state.media.video.isActive ||
                state.media.screen.isActive;
            if (!hasAnyMediaChecked) {
                return false;
            }

            if (state.recAudioType === "RecordingControl") {
                const hasAnyRecordingControlChecked =
                    state.media.audio.isRod ||
                    state.media.video.isRod ||
                    state.media.screen.isRod;
                if (!hasAnyRecordingControlChecked) {
                    return false;
                }
            }


            // Map of media keys to their checked state and ttl
            const mediaChecks = [
                { checked: state.media.isActive, ttl: state.media.ttl },
                { checked: state.media.metadata.isActive, ttl: state.media.metadata.ttl },
                { checked: state.media.audio.isActive, ttl: state.media.audio.ttl },
                { checked: state.media.video.isActive, ttl: state.media.video.ttl },
                { checked: state.media.screen.isActive, ttl: state.media.screen.ttl },
            ];

            // All checked media must have a valid ttl > 0
            for (const item of mediaChecks) {
                if (item.checked && (isNaN(item.ttl) || Number(item.ttl) <= 0 || ((userRules?.license === "13" || state.isEntryVoiceOnly) && item.ttl > 180))) {
                    return false;
                }
            }
        }

        // --- Schedule validation ---
        if (state.useSchedulingFilter && !isScheduleValid(state.schedule)) {
            return false;
        }

        // --- Stop Reasons validation for Bulk and Rod ---
        if (
            (state.recAudioType === "Bulk" && state.bulk.stopReasonRequired && (!state.bulk.stopReasons || state.bulk.stopReasons.trim() === "")) ||
            (state.recAudioType === "Rod" && state.rod.stopReasonRequired && (!state.rod.stopReasons || state.rod.stopReasons.trim() === ""))
        ) {
            return false;
        }

        // --- Stop Reasons validation for RecordingControl ---
        if (
            state.recAudioType === "RecordingControl" &&
            state.recordingControl.stopReasonRequired &&
            (!state.recordingControl.stopReasons || state.recordingControl.stopReasons.trim() === "")
        ) {
            return false;
        }

        // Require start and stop file for all custom announcements (list and default)
        if (isAnyCustomAnnouncementFileMissing()) {
            return false;
        }

        return true;
    };

    const isAnyCustomAnnouncementFileMissing = () => {
        if (
            state.announcementSetting === "PstnAnnouncement" &&
            state.announcementType === "CustomAnnouncement"
        ) {
            // Check default announcement
            if (
                state.defaultCustomAnnouncement &&
                (
                    !state.defaultAnnouncement.start ||
                    state.defaultAnnouncement.start === "" ||
                    !state.defaultAnnouncement.stop ||
                    state.defaultAnnouncement.stop === ""
                )
            ) {
                return true;
            }
            // Check all announcements in the list
            for (const ann of state.announcements) {
                if (
                    !ann.start || ann.start === "" ||
                    !ann.stop || ann.stop === ""
                ) {
                    return true;
                }
            }
        }
        return false;
    };

    const updateState = (key, value) => {
        setState((prevState) => ({
            ...prevState,
            [key]: value,
        }));
        isFormValid();
    };
    const updateNestedState = (key1, key2, newValue) => {
        updateState(
            key1,
            key2
                ? {
                    ...state[key1],
                    [key2]: {
                        ...state[key1]?.[key2],
                        ...newValue,
                    },
                }
                : {
                    ...state[key1],
                    ...newValue,
                }
        );
        isFormValid();
    };

    const addElement = (element) => {
        const maxId = state[element].length === 0 ? -1 : state[element].reduce((max, item) => Math.max(max, item.id), 0);
        const newId = maxId + 1;
        let newElement;
        if (element === "schedule") {
            newElement = {
                id: newId,
                data: {
                    period: "daily",
                    globalStartTime: "00:00:00",
                    globalEndTime: "23:59:59",
                    interval: null,
                    week: {
                        monday: { isActive: false, startTime: null, endTime: null },
                        tuesday: { isActive: false, startTime: null, endTime: null },
                        wednesday: { isActive: false, startTime: null, endTime: null },
                        thursday: { isActive: false, startTime: null, endTime: null },
                        friday: { isActive: false, startTime: null, endTime: null },
                        saturday: { isActive: false, startTime: null, endTime: null },
                        sunday: { isActive: false, startTime: null, endTime: null },
                    },
                },
            };
        } else {
            newElement = { id: newId, data: { entries: null, matchMode: "Match", isActive: false } };
        }
        updateState(element, [...state[element], newElement]);
    };

    const updateElement = (element, id, newData) => {
        const updatedElement = state[element].map((item) => (item.id === id ? { ...item, data: newData } : item));
        updateState(element, updatedElement);
    };

    const deleteElement = (element, id) => {
        const updatedElement = state[element].filter((item) => item.id !== id);
        updateState(element, updatedElement);
    };


    // -----------------------------------------------------------------------------------------------------------------------------------------------
    // ANNOUNCEMENTS FUNCTIONS
    const onUpdatedAnnouncement = (i, announcement, prevFile) => {
        if (state.announcements.length > i) {
            const updatedAnnouncements = state.announcements.map((item, index) => (index !== i ? item : announcement));
            updateState("announcements", updatedAnnouncements);
        } else {
            var announcements = state.announcements;
            announcements.push(announcement);

            updateState("announcements", announcements);
        }
        if (prevFile !== undefined && prevFile !== "") {
            let deletedFiles = state.deletedFilesFromAnnouncements;
            deletedFiles.push(prevFile);
            updateState("deletedFilesFromAnnouncements", deletedFiles);
        }
    };

    const onDeleteAnnouncement = (i, defaultAnn) => {
        let deletedFiles = state.deletedFilesFromAnnouncements;

        if (defaultAnn) {
            let annDefault = state.defaultAnnouncement;

            if (annDefault !== null && annDefault !== undefined && annDefault.start !== "") {
                deletedFiles.push(annDefault.start);
            }
            if (annDefault !== null && annDefault !== undefined && annDefault.stop !== "") {
                deletedFiles.push(annDefault.stop);
            }

            if (document.getElementById("startResDef") !== null && document.getElementById("startResDef") !== undefined) {
                document.getElementById("startResDef").innerHTML = "";
            }
            if (document.getElementById("stopResDef") !== null && document.getElementById("stopResDef") !== undefined) {
                document.getElementById("stopResDef").innerHTML = "";
            }
            if (document.getElementById("stopErrDef") !== null && document.getElementById("stopErrDef") !== undefined) {
                document.getElementById("stopErrDef").innerHTML = "";
            }
            if (document.getElementById("startErrDef") !== null && document.getElementById("startErrDef") !== undefined) {
                document.getElementById("startErrDef").innerHTML = "";
            }

            var defAnnouncementEmpty = {
                prefixList: ["default"],
                start: "",
                stop: "",
            };

            if (state !== undefined && state !== null) {
                updateState("defaultAnnouncement", defAnnouncementEmpty);
                updateState("deletedFilesFromAnnouncements", deletedFiles);
            }
        } else {
            var announcements = state.announcements;

            //create an array with deleted files from deleted announcements
            //this helps in case user deletes an announcement but the doesn't click save
            //files will be deleted only after pressing save to make sure files are not lost if canceled
            if (announcements[i] !== null && announcements[i] !== undefined && announcements[i].start !== "") {
                deletedFiles.push(announcements[i].start);
            }
            if (announcements[i] !== null && announcements[i] !== undefined && announcements[i].stop !== "") {
                deletedFiles.push(announcements[i].stop);
            }

            //remove announcement from list
            announcements.splice(i, 1);
            if (state !== undefined && state !== null) {
                updateState("announcements", announcements);
                updateState("numberPrefixIndex", state.numberPrefixIndex - 1);
                updateState("deletedFilesFromAnnouncements", deletedFiles);
            }
        }
    };

    const uploadToDefault = (file, type) => {
        var ann = state.defaultAnnouncement;
        let prevFile = "";
        let deletedFiles = state.deletedFilesFromAnnouncements;
        if (type === "Start") {
            prevFile = ann.start;
            ann.start = file;
        } else if (type === "Stop") {
            prevFile = ann.stop;
            ann.stop = file;
        }
        if (prevFile !== undefined && prevFile !== "") {
            deletedFiles.push(prevFile);
        }
        updateState("defaultAnnouncement", ann);
        updateState("deletedFilesFromAnnouncements", deletedFiles);
    };

    // Returns filtered start/stop options for RecordingControl
    const getFilteredStartStopOptions = (autoStart, stopReasonRequired, context) => {
        const allOptions = [
            { key: "Unlimited", text: context.getTranslation("recordingRule", "unlimited") },
            { key: "StartOnce", text: context.getTranslation("recordingRule", "startOnce") },
            { key: "StopOnce", text: context.getTranslation("recordingRule", "stopOnce") },
            { key: "StartStopOnce", text: context.getTranslation("recordingRule", "startStopOnce") },
            { key: "StopStartOnce", text: context.getTranslation("recordingRule", "stopStartOnce") },
        ];

        if (autoStart || stopReasonRequired) {
            return allOptions.filter(opt => ["Unlimited", "StopOnce", "StopStartOnce"].includes(opt.key));
        }

        return allOptions.filter(opt => ["Unlimited", "StartOnce", "StartStopOnce"].includes(opt.key));
    };

    // -----------------------------------------------------------------------------------------------------------------------------------------------
    // SAVE FUNCTION
    const handleSaveRule = async () => {
        //call grapqhl

        const profileRule = {
            id: state.id,
            name: state.name, //1
            text: state.description, //1
            isEntryVoiceOnly: state.isEntryVoiceOnly, //1
            callQueueRuleOverride: state.callQueueRuleOverride, //1
            announcements: state.defaultCustomAnnouncement ? state.announcements.concat(state.defaultAnnouncement) : state.announcements,//6
            announcementSetting: state.announcementSetting,//6
            announcementType: state.announcementType,//6
            enableBeepTone: state.enableBeepTone,//6
            beepToneDelay: state.beepToneDelay,//6
            filters: {
                conversationType: state.mediaTypes, //1
                lists: transformStateToList( //2
                    state.participantsList, //2
                    state.callQueueList,//2
                    state.meetingOrganizersList,//2
                    state.meetingAttendeesList,//2
                    state.meetingSubjectList//2
                ),
                recordingType: { //4
                    recAudioType: state.recAudioType,//4
                    bulk: state.bulk,//4
                    rod: state.rod,//4
                    recordingControl: state.recordingControl,//4
                    media: state.media, //5
                    startMode: state.keepDeleteSettings.startMode,//4
                    hasKeepDelete: state.keepDeleteSettings.hasKeepDelete,//4
                    wrapUp: state.wrapUp,//4
                    hasWrapUp: state.hasWrapUp,
                },
                schedulingFilter: { //3
                    hasDurationFilter: state.duration,
                    duration: {
                        max: state.maxDuration, //3
                        min: state.minDuration, //3
                    },
                    timetable: transformStateToTimetable(state.schedule), //3  TREBUIE FIXAT
                },
                statistics: {
                    amountOfConversationsToRecord: getAmountOfConversationsToRecord( //2
                        state.statisticsRecordingType,//2
                        state.amountOfConversations1Input,//2
                        state.amountOfConversations2Input,//2
                        state.amountOfConversations3Input//2
                    ),
                    recordingInterval: state.recordingInterval, //2
                    statisticsRecordingType: state.statisticsRecordingType, //2
                    useSchedulingFilter: state.useSchedulingFilter, //2
                    useStatistics: state.useStatistics,//2
                },
            },
        };
        setState(prevState => ({ ...prevState, graphqlPending: true }));

        try {
            let ruleSave = { name: "", id: "" };

            if (profileRule.id) {
                const response = await apol.mutate({
                    mutation: mutationUpdateUserProfileRule,
                    variables: {
                        input: authorization,
                        profileRule,
                    },
                    refetchQueries: [
                        {
                            query: queryGetConfigData,
                            variables: { auth: authorization },
                        },
                    ],
                });
                const ruleData = response?.data?.updateUserProfileRule;
                ruleSave = {
                    name: ruleData?.name || "",
                    id: ruleData?.id || "",
                };

                if (response?.data?.updateUserProfileRule.name == "error") {
                    toast.error(response?.data?.updateUserProfileRule.text, {
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                } else {
                    toast.success('Profile rule saved successfully', {
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }

            } else {
                const response = await apol.mutate({
                    mutation: mutationCreateUserProfileRule,
                    variables: {
                        input: authorization,
                        profileRule,
                    },
                    refetchQueries: [
                        {
                            query: queryGetConfigData,
                            variables: { auth: authorization },
                        },
                    ],
                });
                const ruleData = response?.data?.createUserProfileRule;
                ruleSave = {
                    name: ruleData?.name || "",
                    id: ruleData?.id || "",
                };

                if (response?.data?.createUserProfileRule.name == "error") {
                    toast.error(response?.data?.createUserProfileRule.text, {
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                } else {
                    toast.success('Profile rule saved successfully', {
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }
            }

            //add the rule inside profile view if created from there
            if (ruleFromProfile) {
                handleAddOrEditRuleFromProfile(ruleSave);
            }


        } catch (error) {
            toast.error(error.status, {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }

        setTimeout(() => setState({ graphqlPending: false }), 1000);

        closePanel();
    };
    // -----------------------------------------------------------------------------------------------------------------------------------------------

    const deleteProfileRule = async () => {
        try {
            await apol.mutate({
                mutation: mutationDeleteUserProfileRule,
                variables: {
                    input: authorization,
                    id: state.id
                },
                refetchQueries: [{
                    query: queryGetConfigData,
                    variables: { auth: authorization },
                }],
            });
            toast.success('Profile rule deleted', {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } catch (error) {
            toast.error('Error deleting profile rule', {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }

        closePanel();
    }

    const disableMeetingCheckboxes = state.isAllMediaTypes && !state.mediaTypes.includes("InternalMeeting") && !state.mediaTypes.includes("ExternalMeeting");

    return (
        <MyContext.Consumer>
            {(context) => (
                <div>
                    {/* FIRST CARD */}
                    <div className={classNames.card}>
                        <div className={classNames.cardHeader}>
                            <div className={classNames.cardNumber}>1</div>
                            <h5 className={classNames.cardTitle}>{context.getTranslation("recordingRule", "details")}</h5>
                        </div>
                        <hr className={classNames.separator} />
                        <div>
                            <TextField
                                type={"text"}
                                label={context.getTranslation("configuration", "name")}
                                value={state.name}
                                onChange={(event, newValue) => {
                                    updateState("name", newValue);
                                }}
                                errorMessage={context.getTranslation("common", nameErrorMessage)}

                                required
                            />
                            <br />
                            {/*see checkbox only if true already or if tenant has license assigned*/}
                            {(tenantData?.licenseEntryVoiceRecording?.bought > 0 || profileRuleData?.isEntryVoiceOnly) && (<>
                                <Checkbox
                                    label={context.getTranslation("configuration", "isEntryVoiceOnly")}
                                    checked={state.isEntryVoiceOnly}
                                    onChange={(event, newValue) => {
                                        updateState("isEntryVoiceOnly", newValue);
                                        if (newValue) {
                                            const capTtl = (val) => (val !== null && val > 180) ? 180 : val;
                                            updateState("media", {
                                                ...state.media,
                                                ttl: capTtl(state.media.ttl),
                                                metadata: { ...state.media.metadata, ttl: capTtl(state.media.metadata.ttl) },
                                                audio: { ...state.media.audio, ttl: capTtl(state.media.audio.ttl) },
                                                video: { ...state.media.video, ttl: capTtl(state.media.video.ttl) },
                                                screen: { ...state.media.screen, ttl: capTtl(state.media.screen.ttl) },
                                            });
                                        }
                                    }}
                                    disabled={profileRuleData?.isEntryVoiceOnly}
                                /></>)}
                                <span style={{ display: 'flex', alignItems: 'center' }} className="cqRuleOverrideRowProfileRule">
                                    <Checkbox
                                        label={context.getTranslation("configuration", "callQueueRuleOverride")}
                                        checked={state.callQueueRuleOverride}
                                        onChange={(event, newValue) => {
                                            updateState("callQueueRuleOverride", newValue);
                                        }}
                                    />
                                    <TooltipHost
                                        content={<>{context.getTranslation("configuration", "callQueueRuleOverrideInfoText")}<br />{context.getTranslation("configuration", "callQueueRuleOverridePrerequisiteText")}</>}
                                        styles={{ root: { marginLeft: 6 } }}
                                        directionalHint={DirectionalHint.topLeftEdge}
                                        calloutProps={{ target: ".cqRuleOverrideRowProfileRule .ms-Checkbox-text", isBeakVisible: true, styles: { beak: { left: beakLeftOverIcon(".cqRuleOverrideRowProfileRule") } } }}
                                    >
                                        <FontIcon aria-label="Info" iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                    </TooltipHost>
                                </span>
                                <br />
                                <Toggle
                                    className={classNames.marginBottom}
                                    label={
                                        <span style={{ display: 'flex', alignItems: 'center' }}>

                                            {state.isAllMediaTypes
                                                ? context.getTranslation("recordingRule", "customConversationTypes")
                                                : context.getTranslation("recordingRule", "allConversationTypes")
                                            }
                                            <TooltipHost
                                                content={context.getTranslation("recordingRule", "conversationTypesTooltip")}
                                                styles={{ root: { marginLeft: 6 } }}
                                            >
                                                <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                            </TooltipHost>
                                        </span>

                                    }
                                    checked={state.isAllMediaTypes}
                                    inlineLabel
                                    onChange={(_, checked) => {
                                        // When ON, select all; when OFF, empty list
                                        toggleMediaType("all", checked);
                                    }}
                                />
                            {state.isAllMediaTypes && (
                                <div className="indent">
                                    <Checkbox
                                        className={classNames.marginBottom}
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                {context.getTranslation("configuration", "inbound")}
                                                <TooltipHost
                                                    content={context.getTranslation("recordingRule", "inboundTooltip")}
                                                    styles={{ root: { marginLeft: 6 } }}
                                                >
                                                    <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                                </TooltipHost>
                                            </span>
                                        }
                                        checked={state.mediaTypes.includes("Inbound")}
                                        onChange={() => toggleMediaType("Inbound")}
                                    />
                                    <Checkbox
                                        className={classNames.marginBottom}
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                {context.getTranslation("configuration", "outbound")}
                                                <TooltipHost
                                                    content={context.getTranslation("recordingRule", "outboundTooltip")}
                                                    styles={{ root: { marginLeft: 6 } }}
                                                >
                                                    <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                                </TooltipHost>
                                            </span>
                                        }
                                        checked={state.mediaTypes.includes("Outbound")}
                                        onChange={() => toggleMediaType("Outbound")}
                                    />
                                    <Checkbox
                                        className={classNames.marginBottom}
                                        label={context.getTranslation("configuration", "internal")}
                                        checked={state.mediaTypes.includes("Internal")}
                                        onChange={() => toggleMediaType("Internal")}
                                    />
                                    <Checkbox
                                        className={classNames.marginBottom}
                                        label={context.getTranslation("configuration", "meetingIntern")}
                                        checked={state.mediaTypes.includes("InternalMeeting")}
                                        onChange={() => toggleMediaType("InternalMeeting")}
                                    />
                                    <Checkbox
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                                {context.getTranslation("configuration", "meetingExtern")}
                                                <TooltipHost
                                                    content={context.getTranslation("recordingRule", "meetingExternTooltip")}
                                                    styles={{ root: { marginLeft: 6 } }}
                                                >
                                                    <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                                </TooltipHost>
                                            </span>
                                        }
                                        checked={state.mediaTypes.includes("ExternalMeeting")}
                                        onChange={() => toggleMediaType("ExternalMeeting")}
                                    />
                                    <br />

                                </div>
                            )}
                            <Label>{context.getTranslation("configuration", "description")}:</Label>
                            <TextField
                                type={"text"}
                                multiline
                                rows="3"
                                cols="75"
                                value={state.description}
                                onChange={(event, newValue) => {
                                    updateState("description", newValue);
                                }}
                            />
                        </div>
                    </div>
                    {/* SECOND CARD */}
                    <div className={classNames.card}>
                        <div className={classNames.cardHeader}>
                            <div className={classNames.cardNumber}>2</div>
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                <h5 className={classNames.cardTitle} style={{ margin: 0 }}>
                                    {context.getTranslation("recordingRule", "filters")}
                                </h5>
                                <TooltipHost
                                    content={context.getTranslation("recordingRule", "filtersTooltip")}
                                    styles={{ root: { marginLeft: 6 } }}
                                >
                                    <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                </TooltipHost>
                            </span>                        </div>
                        <hr className={classNames.separator} />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <Label styles={{ root: { color: "gray", fontSize: "11px", marginTop: "5px", fontWeight: "normal" } }}>
                                {context.getTranslation("recordingRule", "filterInfoMessage")}
                            </Label>
                        </div>
                        <div>
                            {/* Participants List */}
                            {state.participantsList.map((item, key) => (
                                <InputComponent
                                    type="participants"
                                    key={item.id}
                                    index={item.id}
                                    data={item.data}
                                    updateElement={updateElement}
                                    deleteElement={deleteElement}
                                    style={{ marginBottom: "7px" }}
                                />
                            ))}
                            {/*<ActionButton*/}
                            {/*    style={{ marginBottom: "8px" }}*/}
                            {/*    className={classNames.actionButton}*/}
                            {/*    iconProps={{ iconName: "Add" }}*/}
                            {/*    onClick={() => addElement("participantsList")}*/}
                            {/*>*/}
                            {/*    {context.getTranslation("recordingRule", "addParticipantList")}*/}
                            {/*</ActionButton>*/}

                            {/* Call Queue */}
                            {state.callQueueList.map((item, key) => (
                                <InputComponent
                                    type="callQueue"
                                    key={item.id}
                                    index={item.id}
                                    data={item.data}
                                    updateElement={updateElement}
                                    style={{ marginBottom: "7px" }}
                                />
                            ))}
                            {/* Meeting Organizers */}
                            {state.meetingOrganizersList.map((item, key) => (
                                <InputComponent
                                    type="meetingOrganizers"
                                    key={item.id}
                                    data={item.data}
                                    index={item.id}
                                    updateElement={updateElement}
                                    style={{ marginBottom: "7px" }}
                                    disabled={disableMeetingCheckboxes}
                                />
                            ))}
                            {/* Meeting Attendees */}
                            {state.meetingAttendeesList.map((item, key) => (
                                <InputComponent
                                    type="meetingAttendees"
                                    key={item.id}
                                    index={item.id}
                                    data={item.data}
                                    updateElement={updateElement}
                                    deleteElement={deleteElement}
                                    style={{ marginBottom: "7px" }}
                                    disabled={disableMeetingCheckboxes}
                                />
                            ))}
                            {/*<ActionButton*/}
                            {/*    style={{ marginBottom: "8px" }}*/}
                            {/*    className={classNames.actionButton}*/}
                            {/*    iconProps={{ iconName: "Add" }}*/}
                            {/*    onClick={() => addElement("meetingAttendeesList")}*/}
                            {/*>*/}
                            {/*    {context.getTranslation("recordingRule", "addMeetingAttendees")}*/}
                            {/*</ActionButton>*/}

                            {/* Meeting Subject */}
                            {state.meetingSubjectList.map((item, key) => (
                                <InputComponent
                                    type="meetingSubject"
                                    key={item.id}
                                    index={item.id}
                                    data={item.data}
                                    updateElement={updateElement}
                                    deleteElement={deleteElement}
                                    style={{ marginBottom: "20px" }}
                                    disabled={disableMeetingCheckboxes}

                                />
                            ))}
                            {state.useStatistics && ( /*hide statistics, will be implemented later*/
                                <Checkbox
                                    className={classNames.marginBottom}
                                    label={context.getTranslation("recordingRule", "statistics")}
                                    checked={state.useStatistics}
                                    onChange={(event, newValue) => {
                                        updateState("useStatistics", newValue);
                                    }}
                                />
                            )}
                            {state.useStatistics && (
                                <ChoiceGroup
                                    style={{ marginLeft: "30px", marginBottom: "12px" }}
                                    defaultSelectedKey={state.statisticsRecordingType}
                                    options={[
                                        {
                                            key: "EveryXthConversation",
                                            text: context.getTranslation("recordingRule", "every"),
                                            styles: { field: { fontWeight: FontWeights.regular } },
                                            onRenderField: function definition(props, render) {
                                                return (
                                                    <div className={classNames.flexContainer}>
                                                        {render?.(props)}
                                                        <div style={{ marginLeft: "10px" }}>
                                                            <TextField
                                                                style={{ marginLeft: "10px", marginBottom: "6px" }}
                                                                value={state.amountOfConversations1Input}
                                                                className={classNames.numberInput}
                                                                type={"number"}
                                                                min={0}
                                                                onChange={(event, newValue) => {
                                                                    updateState("amountOfConversations1Input", parseInt(newValue));
                                                                }}
                                                                disabled={state.statisticsRecordingType !== "EveryXthConversation"}
                                                            />
                                                        </div>
                                                        <p style={{ marginLeft: "10px", paddingTop: "4px" }}>{context.getTranslation("recordingRule", "thConversation")}</p>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            key: "PercentOfConversations",
                                            text: context.getTranslation("recordingRule", "record"),
                                            styles: { field: { fontWeight: FontWeights.regular } },
                                            onRenderField: function definition(props, render) {
                                                return (
                                                    <div className={classNames.flexContainer}>
                                                        {render?.(props)}
                                                        <div style={{ marginLeft: "10px" }}>
                                                            <TextField
                                                                style={{ marginLeft: "10px", marginBottom: "6px" }}
                                                                value={state.amountOfConversations2Input}
                                                                className={classNames.numberInput}
                                                                type={"number"}
                                                                min={0}
                                                                onChange={(event, newValue) => {
                                                                    updateState("amountOfConversations2Input", parseInt(newValue));
                                                                }}
                                                                disabled={state.statisticsRecordingType !== "PercentOfConversations"}
                                                            />
                                                        </div>
                                                        <p style={{ marginLeft: "10px", paddingTop: "4px" }}>
                                                            {context.getTranslation("recordingRule", "percentageConversation")}
                                                        </p>
                                                    </div>
                                                );
                                            },
                                        },
                                        {
                                            key: "FirstXConversations",
                                            text: context.getTranslation("recordingRule", "recordFirst"),
                                            styles: { field: { fontWeight: FontWeights.regular } },
                                            onRenderField: function definition(props, render) {
                                                return (
                                                    <div className={classNames.flexContainer}>
                                                        {render?.(props)}
                                                        <div style={{ marginLeft: "10px" }}>
                                                            <TextField
                                                                style={{ marginLeft: "10px", marginBottom: "6px" }}
                                                                value={state.amountOfConversations3Input}
                                                                className={classNames.numberInput}
                                                                type={"number"}
                                                                min={0}
                                                                onChange={(event, newValue) => {
                                                                    updateState("amountOfConversations3Input", parseInt(newValue));
                                                                }}
                                                                disabled={state.statisticsRecordingType !== "FirstXConversations"}
                                                            />
                                                        </div>
                                                        <p style={{ marginLeft: "10px", paddingTop: "4px", marginRight: "10px" }}>
                                                            {context.getTranslation("recordingRule", "conversationsPer")}
                                                        </p>
                                                        <Dropdown
                                                            style={{ width: "90px" }}
                                                            selectedKey={state.recordingInterval}
                                                            options={[
                                                                { key: "Day", text: context.getTranslation("common", "day") },
                                                                { key: "Week", text: context.getTranslation("common", "week") },
                                                                { key: "Month", text: context.getTranslation("common", "month") },
                                                            ]}
                                                            onChange={(event, newValue) => {
                                                                updateState("recordingInterval", newValue.key);
                                                            }}
                                                            disabled={state.statisticsRecordingType !== "FirstXConversations"}
                                                        />
                                                    </div>
                                                );
                                            },
                                        },
                                    ]}
                                    onChange={(event, newValue) => {
                                        updateState("statisticsRecordingType", newValue.key);
                                    }}
                                />
                            )}
                            <div>
                                <Checkbox
                                    label={context.getTranslation("recordingRule", "schedulingTime")}
                                    checked={state.useSchedulingFilter}
                                    onChange={(event, newValue) => {
                                        updateState("useSchedulingFilter", newValue);
                                    }}
                                />
                                {state.useSchedulingFilter && <div style={{ marginLeft: "30px" }}>
                                    {state.schedule.map((item) => {
                                        const duplicateObj = isDuplicateArray.find(d => d.id === item.id);
                                        return (
                                            <ProfileRuleScheduleCard
                                                key={item.id}
                                                index={item.id}
                                                data={item.data}
                                                deleteSchedule={deleteElement}
                                                updateSchedule={updateElement}
                                                style={{ marginBottom: "7px" }}
                                                isDuplicate={duplicateObj ? duplicateObj.isDuplicate : false}
                                            />
                                        );
                                    })}
                                    <ActionButton className={classNames.actionButton} iconProps={{ iconName: "Add" }} onClick={() => addElement("schedule")}>
                                        {context.getTranslation("recordingRule", "addSchedule")}
                                    </ActionButton>
                                </div>}
                            </div>

                            {/*--------------------------------HIDE DURATION FUNCTIONALITY FOR NOW-----------------------------------------------------*/}
                            {/*<div style={{ marginTop: "10px", marginBottom: "15px" }}>*/}
                            {/*    <Checkbox*/}
                            {/*        styles={{ root: { marginTop: "5px", marginRight: "10px" } }}*/}
                            {/*        label={context.getTranslation("recordingRule", "duration")}*/}
                            {/*        checked={state.duration}*/}
                            {/*        onChange={(event, newValue) => {*/}
                            {/*            updateState("duration", newValue);*/}
                            {/*            updateState("minDuration", newValue ? "00:00:00" : "");*/}
                            {/*            updateState("maxDuration", newValue ? "00:00:00" : "");*/}
                            {/*        }}*/}
                            {/*    />*/}
                            {/*    {state.duration && <div style={{ marginLeft: "30px" }}>*/}
                            {/*        <div style={{ marginBottom: "7px" }} className={classNames.flexContainer}>*/}
                            {/*            <Text styles={{ root: { marginTop: "5px", marginRight: "10px" } }}>*/}
                            {/*                {context.getTranslation("recordingRule", "longerThan")}*/}
                            {/*            </Text>*/}
                            {/*            <TimePicker*/}
                            {/*                className={classNames.mr10}*/}
                            {/*                value={state.minDuration}*/}
                            {/*                format="HH:mm:ss"*/}
                            {/*                maxDetail="second"*/}
                            {/*                disableClock={true}*/}
                            {/*                clearIcon={null}*/}
                            {/*                hourPlaceholder="hh"*/}
                            {/*                minutePlaceholder="mm"*/}
                            {/*                secondPlaceholder="ss"*/}
                            {/*                onChange={(newValue) => updateState("minDuration", newValue)}*/}
                            {/*            />*/}
                            {/*            <p style={{ margin: "0px 10px" }}>{context.getTranslation("recordingRule", "minimumDuration")} </p>*/}
                            {/*        </div>*/}
                            {/*        <div className={classNames.flexContainer}>*/}
                            {/*            <Text styles={{ root: { marginTop: "5px", marginRight: "10px" } }}>*/}
                            {/*                {context.getTranslation("recordingRule", "shorterThan")}*/}
                            {/*            </Text>*/}
                            {/*            <TimePicker*/}
                            {/*                className={classNames.mr10}*/}
                            {/*                value={state.maxDuration}*/}
                            {/*                format="HH:mm:ss"*/}
                            {/*                maxDetail="second"*/}
                            {/*                disableClock={true}*/}
                            {/*                clearIcon={null}*/}
                            {/*                hourPlaceholder="hh"*/}
                            {/*                minutePlaceholder="mm"*/}
                            {/*                secondPlaceholder="ss"*/}
                            {/*                onChange={(newValue) => updateState("maxDuration", newValue)}*/}
                            {/*            />*/}
                            {/*            <p style={{ margin: "0px 10px" }}>{context.getTranslation("recordingRule", "maximumDuration")} </p>*/}
                            {/*        </div>*/}
                            {/*    </div>}*/}
                            {/*</div>*/}
                            {/*    -------------------------------------------END DURATION-------------------------------------------*/}
                        </div>

                    </div>

                    {/* Third CARD */}
                    <div className={classNames.card}>
                        <div className={classNames.cardHeader}>
                            <div className={classNames.cardNumber}>3</div>
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                                <h5 className={classNames.cardTitle} >
                                    {context.getTranslation("recordingRule", "recordingType")}
                                </h5>
                                <TooltipHost
                                    content={context.getTranslation("recordingRule", "recordingTypeTooltip")}
                                    styles={{ root: { marginLeft: 6 } }}
                                >
                                    <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                </TooltipHost>
                            </span>                        </div>
                        <hr className={classNames.separator} />
                        <div>

                            <ChoiceGroup
                                defaultSelectedKey={state.recAudioType}
                                options={[
                                    {
                                        key: "Never",
                                        text: context.getTranslation("configuration", "neverRecord"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                        onRenderField: function definition(props, render) {
                                            return (
                                                <div style={{ display: "flex", flexDirection: "column" }}>
                                                    {render?.(props)}
                                                    <Text styles={{ root: { marginLeft: 26, color: "gray", fontSize: "11px", marginTop: "-5px" } }}>
                                                        {context.getTranslation("recordingRule", "neverRecordTooltip")}
                                                    </Text>
                                                </div>
                                            );
                                        },
                                    },
                                    {
                                        key: "Bulk",
                                        text: context.getTranslation("configuration", "autoStartRecording"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                    },
                                    {
                                        key: "Rod",
                                        text: context.getTranslation("configuration", "onDemandRecording"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                    },
                                    {
                                        key: "RecordingControl",
                                        text: context.getTranslation("recordingRule", "recControl"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                        onRenderField: function definition(props, render) {
                                            return (
                                                <div style={{ display: "flex", flexDirection: "column" }}>
                                                    {render?.(props)}
                                                    <Text styles={{ root: { marginLeft: 26, color: "gray", fontSize: "11px", marginTop: "-5px" } }}>
                                                        {context.getTranslation("recordingRule", "recControlDescription")}
                                                    </Text>
                                                </div>
                                            );
                                        },
                                    },

                                ]}
                                onChange={(event, newValue) => {
                                    updateState("recAudioType", newValue.key);

                                    // Reset isRod flags if not RecordingControl
                                    if (newValue.key !== "RecordingControl") {
                                        updateState("media", {
                                            ...state.media,
                                            audio: { ...state.media.audio, isRod: false },
                                            video: { ...state.media.video, isRod: false },
                                            screen: { ...state.media.screen, isRod: false },
                                        });
                                    }

                                    if (newValue.key === "Never") {
                                        updateState("media", {
                                            ...state.media,
                                            ttlType: null,
                                            ttl: null,
                                            metadata: { ...state.media.metadata, ttl: null },
                                            audio: { ...state.media.audio, ttl: null, isActive: false },
                                            video: { ...state.media.video, ttl: null, isActive: false },
                                            screen: { ...state.media.screen, ttl: null, isActive: false },
                                        });

                                        updateState("hasWrapUp", false);
                                        updateState("wrapUp", 0);
                                        updateState("keepDeleteSettings", {
                                            ...state.keepDeleteSettings,
                                            hasKeepDelete: false,
                                        });
                                    } else if (newValue.key !== "Never" && state.media.ttlType === null) {
                                        updateState("media", {
                                            ...state.media,
                                            ttlType: "all",
                                            ttl: ((userRules?.license === "13" || state.isEntryVoiceOnly) ? 180 : 720),
                                            metadata: { ...state.media.metadata, ttl: ((userRules?.license === "13" || state.isEntryVoiceOnly) ? 180 : 720) }
                                        });
                                    }
                                    if (newValue.key === "RecordingControl") {
                                        updateNestedState("recordingControl", null, { hasStartStops: true });
                                    }
                                    if (newValue.key === "Rod") {
                                        updateNestedState("rod", null, { hasStartStops: true });
                                    }
                                }}
                            />
                            {/* BULK AND ON DEMAND SETTINGS */}
                            {(state.recAudioType === "Bulk" || state.recAudioType === "Rod") && (
                                <div>
                                    <hr />
                                    <div style={{ padding: "0px 25px" }}>
                                        <div className={classNames.flexContainer}>
                                            <div style={{ marginTop: "10px", marginRight: "20px" }}>
                                                <Checkbox
                                                    className={classNames.marginBottom}
                                                    label={context.getTranslation("recordingRule", "stopStart")}
                                                    disabled={state.recAudioType === "Rod" ? true : false}
                                                    checked={
                                                        state.recAudioType === "Rod"
                                                            ? state.rod.hasStartStops
                                                            : state.recAudioType === "Bulk"
                                                                ? state.bulk.hasStartStops
                                                                : false
                                                    }
                                                    onChange={(event, newValue) => {
                                                        if (state.recAudioType === "Bulk") {
                                                            updateNestedState("bulk", null, { hasStartStops: newValue });
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <ComboBox
                                                style={{ width: "200px" }}
                                                selectedKey={
                                                    state.recAudioType === "Rod"
                                                        ? state.rod.startStops
                                                        : state.recAudioType === "Bulk"
                                                            ? state.bulk.startStops
                                                            : undefined
                                                }
                                                options={
                                                    state.recAudioType === "Rod"
                                                        ? getFilteredStartStopOptions(
                                                            false,
                                                            false,
                                                            context
                                                        )
                                                        : state.recAudioType === "Bulk"
                                                            ? getFilteredStartStopOptions(
                                                                true,
                                                                false,
                                                                context
                                                            )
                                                            : []
                                                }
                                                onChange={(event, newValue) => {
                                                    if (state.recAudioType === "Rod") {
                                                        updateNestedState("rod", null, { startStops: newValue.key });
                                                    } else if (state.recAudioType === "Bulk") {
                                                        updateNestedState("bulk", null, { startStops: newValue.key });
                                                    }
                                                }}
                                                disabled={
                                                    state.recAudioType === "Rod"
                                                        ? !state.rod.hasStartStops
                                                        : state.recAudioType === "Bulk"
                                                            ? !state.bulk.hasStartStops
                                                            : true
                                                } />
                                        </div>
                                        {!((state.recAudioType === "Rod" && state.rod.startStops === "StartOnce") || (state.recAudioType === "Bulk" && !state.bulk.hasStartStops)) && <>

                                            <Checkbox
                                                label={context.getTranslation("recordingRule", "stopReason")}
                                                checked={
                                                    state.recAudioType === "Rod"
                                                        ? state.rod.stopReasonRequired
                                                        : state.recAudioType === "Bulk"
                                                            ? state.bulk.stopReasonRequired
                                                            : false
                                                }
                                                onChange={(event, newValue) => {
                                                    if (state.recAudioType === "Rod") {
                                                        updateNestedState("rod", null, { stopReasonRequired: newValue });
                                                    } else if (state.recAudioType === "Bulk") {
                                                        updateNestedState("bulk", null, { stopReasonRequired: newValue });
                                                    }
                                                }}
                                            />
                                            {(state.recAudioType === "Rod"
                                                ? state.rod.stopReasonRequired
                                                : state.recAudioType === "Bulk"
                                                    ? state.bulk.stopReasonRequired
                                                    : false) && (
                                                    <div style={{ marginBottom: "15px", marginLeft: "30px" }}>
                                                        <TextField
                                                            type={"text"}
                                                            label={context.getTranslation("recordingRule", "stopReasonsLabel")}
                                                            onChange={(event, newValue) => {
                                                                if (state.recAudioType === "Rod") {
                                                                    updateNestedState("rod", null, { stopReasons: newValue });
                                                                } else if (state.recAudioType === "Bulk") {
                                                                    updateNestedState("bulk", null, { stopReasons: newValue });
                                                                }
                                                            }}
                                                            value={
                                                                state.recAudioType === "Rod"
                                                                    ? state.rod.stopReasons
                                                                    : state.recAudioType === "Bulk"
                                                                        ? state.bulk.stopReasons
                                                                        : ""
                                                            }
                                                            description={context.getTranslation("recordingRule", "stopReasonsDescription")}
                                                            required
                                                        />
                                                    </div>
                                                )}
                                        </>}
                                        {state.recAudioType === "Bulk" && (
                                            <div className={classNames.flexContainer}>
                                                <div style={{ marginTop: "5px", marginRight: "10px" }}>
                                                    <Checkbox
                                                        label={context.getTranslation("recordingRule", "recordingStartsAfter")}
                                                        checked={state.bulk.recordingDelayEnabled}
                                                        onChange={(event, newValue) => {
                                                            updateNestedState("bulk", null, { recordingDelayEnabled: newValue });
                                                        }}
                                                    />
                                                </div>
                                                <TextField
                                                    value={state.bulk.recordingStartsAfter}
                                                    className={classNames.numberInput}
                                                    type={"number"}
                                                    min={0}
                                                    onChange={(event, newValue) => {
                                                        updateNestedState("bulk", null, { recordingStartsAfter: parseInt(newValue, 10) });
                                                    }}
                                                    disabled={!state.bulk.recordingDelayEnabled}
                                                />
                                                <p style={{ margin: "0px 10px" }}>{context.getTranslation("common", "seconds")} </p>
                                            </div>
                                        )}
                                    </div>
                                    <hr />
                                </div>
                            )}
                            {/* RECORDING CONTROL ON MEDIA LEVEL SETTINGS */}
                            {state.recAudioType === "RecordingControl" && (
                                <div>
                                    <hr />
                                    <div style={{ padding: "0px 25px" }}>
                                        <div className={classNames.marginBottom}>
                                            <Text>{context.getTranslation("recordingRule", "bulkRecDescriptionTitle")}</Text>
                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                <Text styles={{ root: { color: "gray", fontSize: "11px", marginTop: "5px" } }}>
                                                    {context.getTranslation("recordingRule", "bulkRecdescription")}
                                                </Text>
                                            </div>
                                        </div>
                                        <div className={classNames.flexContainer}>


                                            <Text styles={{ root: { marginTop: "5px", marginRight: "10px" } }}>
                                                {context.getTranslation("recordingRule", "recStartsAfter")}
                                            </Text>

                                            <TextField
                                                value={state.recordingControl.recordingStartsAfter}
                                                className={classNames.numberInput}
                                                type={"number"}
                                                min={0}
                                                onChange={(event, newValue) => {
                                                    updateNestedState("recordingControl", null, { recordingStartsAfter: parseInt(newValue, 10) });
                                                }}
                                            //disabled={!state.recordingControl.recordingDelayEnabled}
                                            />
                                            <p style={{ margin: "0px 10px" }}>{context.getTranslation("common", "seconds")} </p>
                                        </div>

                                        <Label className={classNames.marginBottom} >
                                            {context.getTranslation("recordingRule", "recordingControlDefinition")}
                                        </Label>
                                        <div style={{ marginTop: "3px", marginBottom: "10px" }} className={classNames.flexContainer}>


                                            <div style={{ marginLeft: "30px" }}>
                                                <Checkbox
                                                    className={classNames.marginBottom}
                                                    label={context.getTranslation("configuration", "autoStartRecording")}
                                                    checked={state.recordingControl.autoStartRecording}
                                                    onChange={(event, newValue) => {
                                                        updateNestedState("recordingControl", null, {
                                                            autoStartRecording: newValue,
                                                            startStops: "Unlimited"
                                                        });
                                                    }}
                                                />

                                                <div className={classNames.flexContainer}>

                                                    <div style={{ marginTop: "10px", marginRight: "20px" }}>

                                                        <Checkbox
                                                            className={classNames.marginBottom}
                                                            label={context.getTranslation("recordingRule", "stopStart")}
                                                            checked={state.recordingControl.hasStartStops}
                                                            onChange={(event, newValue) => {
                                                                updateNestedState("recordingControl", null, { hasStartStops: newValue });

                                                            }}
                                                            disabled={true}

                                                        />
                                                    </div>
                                                    <ComboBox
                                                        style={{ width: "200px" }}
                                                        selectedKey={state.recordingControl.startStops}
                                                        options={getFilteredStartStopOptions(
                                                            state.recordingControl.autoStartRecording,
                                                            state.recordingControl.stopReasonRequired,
                                                            context
                                                        )} onChange={(event, newValue) => {
                                                            updateNestedState("recordingControl", null, { startStops: newValue.key });
                                                        }}
                                                    />
                                                </div>

                                                <Checkbox
                                                    label={context.getTranslation("recordingRule", "stopReason")}
                                                    checked={state.recAudioType === "RecordingControl" ? state.recordingControl.stopReasonRequired : false}
                                                    className={classNames.marginTop}
                                                    onChange={(event, newValue) => {
                                                        updateNestedState("recordingControl", null, {
                                                            stopReasonRequired: newValue,
                                                            startStops: "Unlimited"
                                                        });
                                                    }}
                                                />
                                                {state.recAudioType === "RecordingControl" && state.recordingControl.stopReasonRequired && (
                                                    <div style={{ marginBottom: "15px", marginLeft: "30px" }}>
                                                        <TextField
                                                            type={"text"}
                                                            label={context.getTranslation("recordingRule", "stopReasonsLabel")}
                                                            onChange={(event, newValue) => {
                                                                updateNestedState("recordingControl", null, { stopReasons: newValue });
                                                            }}
                                                            value={state.recordingControl.stopReasons || ""}
                                                            description={context.getTranslation("recordingRule", "stopReasonsDescription")}
                                                            required
                                                        />
                                                    </div>
                                                )}

                                            </div>

                                        </div>

                                    </div>
                                    <hr />
                                </div>
                            )}
                            {/* KEEP / DELETE SETTINGS */}
                            <div style={{ display: "flex" }}>
                                <Checkbox
                                    checked={state.keepDeleteSettings.hasKeepDelete}
                                    label={context.getTranslation("configuration", "keepDelete")}
                                    onChange={(event, newValue) => {
                                        updateState("keepDeleteSettings", {
                                            ...state.keepDeleteSettings,
                                            hasKeepDelete: newValue,
                                        });
                                    }}
                                    disabled={state.recAudioType === "Never"}

                                />

                                <TooltipHost
                                    content={<><span style={{ fontWeight: "bold" }}>{context.getTranslation("common", "warning")}</span>: {context.getTranslation("recordingRule", "keepDeleteTooltip")}</>}
                                    styles={{ root: { marginLeft: 6 } }}
                                >
                                    <FontIcon iconName="Info" style={{ cursor: 'pointer', fontSize: 14 }} />
                                </TooltipHost>
                            </div>

                            {state.keepDeleteSettings.hasKeepDelete && (
                                <div>
                                    <div style={{ padding: "0px 25px" }}>
                                        <ChoiceGroup
                                            style={{ marginBottom: "10px" }}
                                            defaultSelectedKey={state.keepDeleteSettings.startMode}
                                            options={[
                                                {
                                                    key: "Keep",
                                                    text: context.getTranslation("configuration", "keep"),
                                                    styles: { field: { fontWeight: FontWeights.regular } },
                                                },
                                                {
                                                    key: "Delete",
                                                    text: context.getTranslation("configuration", "delete"),
                                                    styles: { field: { fontWeight: FontWeights.regular } },
                                                },
                                            ]}
                                            onChange={(event, newValue) => {
                                                updateState("keepDeleteSettings", {
                                                    ...state.keepDeleteSettings,
                                                    startMode: newValue.key,
                                                });
                                            }}
                                        />
                                    </div>
                                    <hr />
                                </div>
                            )}
                            {/* WRAP UP*/}
                            <div className={classNames.flexContainer}>
                                <Checkbox
                                    styles={{ root: { marginTop: "5px", marginRight: "10px" } }}
                                    label={context.getTranslation("recordingRule", "wrapUp")}
                                    checked={state.hasWrapUp}
                                    onChange={(event, newValue) => {
                                        updateState("hasWrapUp", newValue);
                                        updateState("wrapUp", newValue ? 30 : 0);
                                    }}
                                    disabled={state.recAudioType === "Never"}
                                />

                                <TextField
                                    value={state.wrapUp}
                                    className={classNames.numberInput}
                                    type={"number"}
                                    min={0}
                                    onChange={(event, newValue) => {
                                        const wrapupValue = parseInt(newValue);
                                        updateState("wrapUp", wrapupValue);
                                    }}
                                    errorMessage={state.hasWrapUp && (state.wrapUp === 0 || state.wrapUp === null || isNaN(state.wrapUp)) ? context.getTranslation("common", "minValue") : ""}
                                    disabled={!state.hasWrapUp}
                                />

                                <p style={{ margin: "0px 10px" }}>{context.getTranslation("common", "seconds")} </p>
                            </div>
                        </div>
                    </div>
                    {/* Fourth CARD */}
                    <div className={classNames.card}>
                        <div className={classNames.cardHeader}>
                            <div className={classNames.cardNumber}>4</div>
                            <h5 className={classNames.cardTitle}>{context.getTranslation("recordingRule", "recMediaAndRetention")}</h5>
                        </div>
                        <hr className={classNames.separator} />
                        {state.recAudioType !== "Never" && (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <Label required styles={{ root: { color: "gray", fontSize: "11px", marginTop: "5px", fontWeight: "normal" } }}>
                                    {context.getTranslation("recordingRule", "requiredMediaMessage")}
                                </Label>
                                {state.recAudioType === "RecordingControl" && <Label styles={{ root: { color: "gray", fontSize: "11px", marginTop: "5px", fontWeight: "normal" } }}>
                                    {context.getTranslation("recordingRule", "recControlMessage")}
                                </Label>}
                            </div>
                        )}

                        <div className={classNames.marginBottom}>
                            <div className={classNames.columnGrid3}>
                                <div>
                                    {state.recAudioType !== "Never" && !state.media.audio.isActive && !state.media.video.isActive && !state.media.screen.isActive &&
                                        <Text
                                            styles={{ root: { color: "#a4262c", fontSize: 12 } }}>
                                            {context.getTranslation("recordingRule", "selectMedia")}
                                        </Text>
                                    }
                                </div>
                                <div>
                                    {state.recAudioType === "RecordingControl"
                                        && (
                                            (state.media.audio.isActive || state.media.video.isActive || state.media.screen.isActive)
                                            && !state.media.audio.isRod && !state.media.video.isRod && !state.media.screen.isRod) &&
                                        (< Text
                                            styles={{ root: { color: "#a4262c", fontSize: 12 } }}>
                                            {context.getTranslation("recordingRule", "selectRecordingControl")}
                                        </Text>
                                        )}
                                </div>

                            </div>
                        </div>
                        {/* End Media Item */}
                        <div className={classNames.marginBottom}>
                            <div className={classNames.columnGrid3}>
                                <Checkbox
                                    className={classNames.flexContainerEnd}
                                    label={context.getTranslation("recordingRule", "audio")}
                                    checked={state.media.audio.isActive}
                                    disabled={state.recAudioType === "Never"}
                                    onChange={(event, newValue) => {
                                        updateNestedState("media", "audio", {
                                            ttl:
                                                state.media.ttl !== null
                                                    ? state.media.ttl
                                                    : state.media.audio.ttl !== null
                                                        ? state.media.audio.ttl
                                                        : state.media.metadata.ttl,
                                            isActive: newValue,
                                            isRod: newValue ? state.media.audio.isRod : false

                                        });
                                    }}
                                />
                                {state.recAudioType === "RecordingControl" &&
                                    <Checkbox
                                        className={classNames.flexContainerEnd}
                                        label={context.getTranslation("recordingRule", "recordingControl")}
                                        checked={state.media.audio.isRod}
                                        disabled={!(state.media.audio.isActive && state.recordingControl.startStops && state.recAudioType === "RecordingControl")}
                                        onChange={(event, newValue) => {
                                            updateNestedState("media", "audio", { isRod: newValue });
                                        }}
                                    />
                                }
                            </div>
                        </div>

                        {/* End Media Item */}
                        <div className={classNames.marginBottom}>
                            <div className={classNames.columnGrid3}>
                                <Checkbox
                                    className={classNames.flexContainerEnd}
                                    label={context.getTranslation("recordingRule", "video")}
                                    checked={state.media.video.isActive}
                                    disabled={state.recAudioType === "Never"}
                                    onChange={(event, newValue) => {

                                        updateNestedState("media", "video", {
                                            ttl:
                                                state.media.ttl !== null
                                                    ? state.media.ttl
                                                    : state.media.video.ttl !== null
                                                        ? state.media.video.ttl
                                                        : state.media.metadata.ttl,
                                            isActive: newValue,
                                            isRod: newValue ? state.media.video.isRod : false

                                        });
                                    }}
                                />
                                {state.recAudioType === "RecordingControl" &&

                                    <Checkbox
                                        className={classNames.flexContainerEnd}
                                        label={context.getTranslation("recordingRule", "recordingControl")}
                                        checked={state.media.video.isRod}
                                        disabled={!(state.media.video.isActive && state.recordingControl.startStops && state.recAudioType === "RecordingControl")}
                                        onChange={(event, newValue) => {
                                            updateNestedState("media", "video", { isRod: newValue });
                                        }}
                                    />
                                }
                            </div>
                        </div>

                        {/* End Media Item */}
                        <div className={classNames.marginBottom}>
                            <div className={classNames.columnGrid3}>
                                <Checkbox
                                    className={classNames.flexContainerEnd}
                                    label={context.getTranslation("recordingRule", "screenShare")}
                                    checked={state.media.screen.isActive}
                                    disabled={state.recAudioType === "Never"}
                                    onChange={(event, newValue) => {

                                        updateNestedState("media", "screen", {
                                            ttl:
                                                state.media.ttl !== null
                                                    ? state.media.ttl
                                                    : state.media.screen.ttl !== null
                                                        ? state.media.screen.ttl
                                                        : state.media.metadata.ttl,
                                            isActive: newValue,
                                            isRod: newValue ? state.media.screen.isRod : false

                                        });
                                    }}
                                />
                                {state.recAudioType === "RecordingControl" &&

                                    <Checkbox
                                        className={classNames.flexContainerEnd}
                                        label={context.getTranslation("recordingRule", "recordingControl")}
                                        checked={state.media.screen.isRod}
                                        disabled={!(state.media.screen.isActive && state.recordingControl.startStops && state.recAudioType === "RecordingControl")}
                                        onChange={(event, newValue) => {
                                            updateNestedState("media", "screen", { isRod: newValue });
                                        }}
                                    />
                                }
                            </div>
                        </div>


                        {/*choose retention time*/}

                        <div className={classNames.marginBottom}>
                            <ChoiceGroup
                                selectedKey={state.media.ttlType}
                                disabled={state.recAudioType === "Never"}
                                options={[
                                    {
                                        key: "all",
                                        text: context.getTranslation("recordingRule", "allMedia"),
                                        onRenderField: function definition(props, render) {
                                            return (
                                                <div >
                                                    <div>{render(props)}</div>
                                                    {state.media.ttlType === "all" && (
                                                        <div className={classNames.flexContainer} style={{ padding: "0px 15px" }}>
                                                            <p style={{ margin: "0px 10px" }}>
                                                                {context.getTranslation("recordingRule", "retentionTime")}
                                                            </p>
                                                            <TextField
                                                                value={state.media.ttl}
                                                                className={classNames.numberInput}
                                                                type={"number"}
                                                                min={0}
                                                                max={(userRules.license === "13" || state.isEntryVoiceOnly) ? 180 : undefined}
                                                                disabled={state.recAudioType === "Never"}
                                                                errorMessage={
                                                                    (state.media.ttl === 0 || state.media.ttl === null || isNaN(state.media.ttl))
                                                                        ? context.getTranslation("common", "minValue")
                                                                        : ((userRules.license === "13" || state.isEntryVoiceOnly) && state.media.ttl > 180)
                                                                            ? "Maximum 180 days allowed"
                                                                            : ""
                                                                }
                                                                onChange={(event, newValue) => {
                                                                    let ttlValue = parseInt(newValue);
                                                                    if ((userRules.license === "13" || state.isEntryVoiceOnly) && ttlValue > 180) {
                                                                        ttlValue = 180;
                                                                    }
                                                                    updateState("media", {
                                                                        ...state.media,
                                                                        ttl: ttlValue,
                                                                        metadata: { ...state.media.metadata, ttl: ttlValue },
                                                                        audio: state.media.audio.isActive ? { ...state.media.audio, ttl: ttlValue } : state.media.audio,
                                                                        video: state.media.video.isActive ? { ...state.media.video, ttl: ttlValue } : state.media.video,
                                                                        screen: state.media.screen.isActive ? { ...state.media.screen, ttl: ttlValue } : state.media.screen,
                                                                    });
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        },
                                    },
                                    {
                                        key: "custom",
                                        text: context.getTranslation("recordingRule", "customRetentionTime"),
                                    },
                                ]}
                                onChange={(event, option) => {
                                    if (option.key === "custom") {
                                        updateState("media", {
                                            ...state.media,
                                            ttlType: "custom",
                                            ttl: null,

                                        });
                                    } else if (option.key === "all") {
                                        const defaultTtl = ((userRules?.license === "13" || state.isEntryVoiceOnly) ? 180 : 720);
                                        updateState("media", {
                                            ...state.media,
                                            ttlType: "all",
                                            ttl: defaultTtl,
                                            metadata: { ...state.media.metadata, ttl: defaultTtl },
                                            audio: state.media.audio.isActive ? { ...state.media.audio, ttl: defaultTtl } : state.media.audio,
                                            video: state.media.video.isActive ? { ...state.media.video, ttl: defaultTtl } : state.media.video,
                                            screen: state.media.screen.isActive ? { ...state.media.screen, ttl: defaultTtl } : state.media.screen,
                                        });
                                    } else {
                                        updateNestedState("media", null, { ttlType: option.key });
                                    }
                                }}
                            />


                        </div>



                        {state.media.ttlType === "custom" && (
                            <div style={{ padding: "0px 25px" }}>
                                <div className={`${classNames.flexContainer} ${classNames.marginBottom}`}>
                                    <Label style={{ minWidth: 120 }}>
                                        {context.getTranslation("recordingRule", "metadata")}
                                    </Label>
                                    <p style={{ margin: "0px 10px" }}>{context.getTranslation("recordingRule", "retentionTime")} </p>
                                    <TextField
                                        value={state.media.metadata.ttl}
                                        className={classNames.numberInput}
                                        type={"number"}
                                        min={0}
                                        max={(userRules.license === "13" || state.isEntryVoiceOnly) ? 180 : undefined}
                                        errorMessage={
                                            getMetadataTtlError(
                                                state.media.metadata.ttl,
                                                state.media.audio.ttl,
                                                state.media.video.ttl,
                                                state.media.screen.ttl,
                                                context,
                                                state.media.audio.isActive,
                                                state.media.video.isActive,
                                                state.media.screen.isActive,
                                                userRules.license,
                                                state.isEntryVoiceOnly
                                            )
                                        } onChange={(event, newValue) => {
                                            updateNestedState("media", "metadata", { ttl: parseInt(newValue) })
                                        }}
                                    //disabled={state.media.audio.ttl === null || state.media.ttl !== null}
                                    />
                                </div>
                                {state.media.audio.isActive && (
                                    <div className={`${classNames.flexContainer} ${classNames.marginBottom}`}>
                                        <Label style={{ minWidth: 120 }}>
                                            {context.getTranslation("recordingRule", "audio")}
                                        </Label>
                                        <p style={{ margin: "0px 10px" }}>{context.getTranslation("recordingRule", "retentionTime")} </p>
                                        <TextField
                                            value={state.media.audio.ttl}
                                            className={classNames.numberInput}
                                            type={"number"}
                                            min={0}
                                            max={(userRules.license === "13" || state.isEntryVoiceOnly) ? 180 : undefined}
                                            errorMessage={getMediaTtlError(state.media.audio.ttl, state.media.metadata.ttl, context, state.media.audio.isActive, userRules.license, state.isEntryVoiceOnly)}
                                            onChange={(event, newValue) => {
                                                let ttlValue = parseInt(newValue);
                                                if ((userRules.license === "13" || state.isEntryVoiceOnly) && ttlValue > 180) {
                                                    ttlValue = 180;
                                                }
                                                updateNestedState("media", "audio", { ttl: ttlValue });
                                            }}
                                        //disabled={state.media.audio.ttl === null || state.media.ttl !== null}
                                        />
                                    </div>
                                )}
                                {state.media.video.isActive && (
                                    <div className={`${classNames.flexContainer} ${classNames.marginBottom}`}>
                                        <Label style={{ minWidth: 120 }}>
                                            {context.getTranslation("recordingRule", "video")}
                                        </Label>
                                        <p style={{ margin: "0px 10px" }}>{context.getTranslation("recordingRule", "retentionTime")} </p>
                                        <TextField
                                            value={state.media.video.ttl}
                                            className={classNames.numberInput}
                                            type={"number"}
                                            min={0}
                                            max={(userRules.license === "13" || state.isEntryVoiceOnly) ? 180 : undefined}
                                            errorMessage={getMediaTtlError(state.media.video.ttl, state.media.metadata.ttl, context, state.media.video.isActive, userRules.license, state.isEntryVoiceOnly)}
                                            onChange={(event, newValue) => {
                                                let ttlValue = parseInt(newValue);
                                                if ((userRules.license === "13" || state.isEntryVoiceOnly) && ttlValue > 180) {
                                                    ttlValue = 180;
                                                }
                                                updateNestedState("media", "video", { ttl: ttlValue });
                                            }}
                                        //disabled={state.media.video.ttl === null || state.media.ttl !== null}
                                        />
                                    </div>
                                )}
                                {state.media.screen.isActive && (
                                    <div className={classNames.flexContainer}>
                                        <Label style={{ minWidth: 120 }}>
                                            {context.getTranslation("recordingRule", "screenShare")}
                                        </Label>
                                        <p style={{ margin: "0px 10px" }}>{context.getTranslation("recordingRule", "retentionTime")} </p>
                                        <TextField
                                            value={state.media.screen.ttl}
                                            className={classNames.numberInput}
                                            type={"number"}
                                            min={0}
                                            max={(userRules.license === "13" || state.isEntryVoiceOnly) ? 180 : undefined}
                                            errorMessage={getMediaTtlError(state.media.screen.ttl, state.media.metadata.ttl, context, state.media.screen.isActive, userRules.license, state.isEntryVoiceOnly)}
                                            onChange={(event, newValue) => {
                                                let ttlValue = parseInt(newValue);
                                                if ((userRules.license === "13" || state.isEntryVoiceOnly) && ttlValue > 180) {
                                                    ttlValue = 180;
                                                }
                                                updateNestedState("media", "screen", { ttl: ttlValue });
                                            }}
                                        //disabled={state.media.screen.ttl === null || state.media.ttl !== null}
                                        />
                                    </div>
                                )}
                            </div>
                        )}


                    </div>
                    {/* Fifth CARD */}
                    <div className={classNames.card}>
                        <div className={classNames.cardHeader}>
                            <div className={classNames.cardNumber}>5</div>
                            <h5 className={classNames.cardTitle}>{context.getTranslation("recordingRule", "announcement")}</h5>
                        </div>
                        <hr className={classNames.separator} />
                        <div>
                            <ChoiceGroup
                                className={classNames.marginBottom}
                                defaultSelectedKey={state.announcementSetting}
                                options={[
                                    {
                                        key: "Profile",
                                        text: context.getTranslation("configuration", "applyProfileSettings"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                    },
                                    {
                                        key: "DefaultMsft",
                                        text: context.getTranslation("configuration", "microsoftDefault"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                    },
                                    {
                                        key: "PstnAnnouncement",
                                        text: context.getTranslation("configuration", "pstnAnnouncement"),
                                        disabled: ((!state.mediaTypes.includes("Inbound") && !state.mediaTypes.includes("Outbound")) && state.mediaTypes.length !== 0),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                    },
                                    {
                                        key: "SilentRecording",
                                        text: context.getTranslation("configuration", "noAnnouncement"),
                                        styles: { field: { fontWeight: FontWeights.regular } },
                                    }
                                ]}
                                onChange={(event, newValue) => {
                                    updateState("announcementSetting", newValue.key);
                                }}
                            />
                            {state.announcementSetting === "PstnAnnouncement" && (
                                <div style={{ marginLeft: "30px" }}>
                                    <ChoiceGroup
                                        selectedKey={state.announcementType}
                                        style={{ width: "300px" }}
                                        options={[
                                            {
                                                key: "DefaultAnnouncement",
                                                text: context.getTranslation("configuration", "RIDefaultAnnouncement"),
                                                "data-automation-id": "auto1",
                                                styles: { field: { fontWeight: FontWeights.regular } },
                                            },
                                            {
                                                key: "CustomAnnouncement",
                                                text: context.getTranslation("configuration", "customAnnouncement"),
                                                styles: { field: { fontWeight: FontWeights.regular } },
                                            },
                                        ]}
                                        onChange={(event, newValue) => {
                                            updateState("announcementType", newValue.key);
                                        }}
                                    />
                                    {state.announcementType === "CustomAnnouncement" && (
                                        <div style={{ marginLeft: "30px" }}>
                                            {isAnyCustomAnnouncementFileMissing() && (
                                                <MessageBar
                                                    messageBarType={MessageBarType.error}
                                                    isMultiline={false}
                                                    styles={{ root: { marginBottom: 10 } }}
                                                >
                                                    {context.getTranslation("recordingRule", "pleaseUploadStartFile")}
                                                </MessageBar>
                                            )}
                                            {state.announcements.map((item, key) => (
                                                <NumberPrefix
                                                    id={key}
                                                    key={key}
                                                    announcement={item}
                                                    styles={styles}
                                                    deleteAnnouncement={onDeleteAnnouncement}
                                                    updateAnnouncement={onUpdatedAnnouncement}
                                                    authorization={authorization}
                                                    client={apol}
                                                />
                                            ))}
                                            <CommandBarButton
                                                style={{ padding: "5px 0px", marginBottom: "30px" }}
                                                iconProps={{ iconName: "Add" }}
                                                text={context.getTranslation("configuration", "addNumberPrefix")}
                                                onClick={() => {
                                                    const newAnnouncement = { prefixList: [], start: "", stop: "" };
                                                    updateState("announcements", [...state.announcements, newAnnouncement]);
                                                }}
                                            />
                                            <Checkbox
                                                style={{ marginBottom: "200px !important" }}
                                                className={classNames.marginBottom}
                                                label={context.getTranslation("configuration", "defaultCustomAnnouncement")}
                                                checked={state.defaultCustomAnnouncement}
                                                onChange={(event, newValue) => {
                                                    updateState("defaultCustomAnnouncement", newValue);
                                                }}
                                            />
                                            {state.defaultCustomAnnouncement && (
                                                <div>
                                                    <div className={classNames.flexContainer} style={{ marginBottom: "5px" }}>
                                                        <IconButton
                                                            style={{ marginLeft: "-7px" }}
                                                            iconProps={{ iconName: "Delete" }}
                                                            onClick={() => {
                                                                onDeleteAnnouncement("", true);
                                                            }}
                                                        />
                                                        <p style={{ marginTop: "10px" }}>{context.getTranslation("configuration", "removeAnnouncement")}</p>
                                                    </div>
                                                    <UploadAnnouncement
                                                        authorization={authorization}
                                                        apol={apol}
                                                        fileBtnText={state.defaultAnnouncement.start === "" ? "Start file" : state.defaultAnnouncement.start.slice(36)}
                                                        idResult="startResDef"
                                                        idError="startErrDef"
                                                        idFiles="startFileDef"
                                                        updateAnnouncement={uploadToDefault}
                                                        type="Start"
                                                        file={state.defaultAnnouncement.start}
                                                    />
                                                    <UploadAnnouncement
                                                        authorization={authorization}
                                                        apol={apol}
                                                        fileBtnText={state.defaultAnnouncement.stop === "" ? "Stop file" : state.defaultAnnouncement.stop.slice(36)}
                                                        idResult="stopResDef"
                                                        idError="stopErrDef"
                                                        idFiles="stopFileDef"
                                                        updateAnnouncement={uploadToDefault}
                                                        type="Stop"
                                                        file={state.defaultAnnouncement.stop}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div style={{ marginTop: "15px" }}>
                                        <Checkbox
                                            label={context.getTranslation("configuration", "ascBeeptone")}
                                            checked={state.enableBeepTone}
                                            onChange={(event, newValue) => {
                                                updateState("enableBeepTone", newValue);
                                            }}
                                        />
                                        <div style={{ display: "flex", marginLeft: "28px" }}>
                                            <span style={{ marginRight: "5px", marginTop: "5px" }}>{context.getTranslation("configuration", "repeatEvery")}</span>
                                            <TextField
                                                type={"number"}
                                                min={5}
                                                className={classNames.numberInput}
                                                value={state.beepToneDelay}
                                                onChange={(event, newValue) => {
                                                    updateState("beepToneDelay", parseInt(newValue));
                                                }}
                                                disabled={!state.enableBeepTone}
                                            />
                                            <span style={{ marginLeft: "5px", marginTop: "5px" }}>{context.getTranslation("common", "seconds")}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="buttons-container">
                        <div>
                            <PrimaryButton
                                text={state.id ? context.getTranslation("common", "save") : context.getTranslation("common", "create")}
                                onClick={handleSaveRule}
                                disabled={!isFormValid() || accessProfiles === false || state.graphqlPending}
                            />
                            <DefaultButton
                                id="deleteProfileRuleBtn"
                                onClick={() => setState(prev => ({ ...prev, showDeleteWarning: true }))}
                                text={context.getTranslation("common", "delete")}
                                className="delete-button button-spacing"
                                disabled={accessProfiles === false}
                            />
                            <DefaultButton
                                text={context.getTranslation("common", "close")}
                                className="button-spacing"
                                onClick={closePanel}
                            />
                            {state.showDeleteWarning && (
                                <TeachingBubble
                                    target="#deleteProfileRuleBtn"
                                    headline={context.getTranslation("common", "warning")}
                                    primaryButtonProps={{
                                        children: context.getTranslation("common", "yes"),
                                        onClick: () => {
                                            setState(prev => ({ ...prev, showDeleteWarning: false }));
                                            deleteProfileRule();
                                        }
                                    }}
                                    secondaryButtonProps={{
                                        children: context.getTranslation("common", "no"),
                                        onClick: () => setState(prev => ({ ...prev, showDeleteWarning: false }))
                                    }}
                                    onDismiss={() => setState(prev => ({ ...prev, showDeleteWarning: false }))}
                                >
                                    {context.getTranslation("recordingRule", "deleteRuleWarning")}
                                </TeachingBubble>
                            )}
                        </div>
                    </div>
                </div>
            )
            }
        </MyContext.Consumer >
    );
};

export default ProfileRuleSettings;
